import { useEffect, useMemo, useReducer, useRef } from "react";
/**
 * A hook that creates a proxy around a state object, allowing for mutable updates
 * while optimizing re-renders.
 *
 * It tracks which properties are accessed during render and only triggers a re-render
 * when those specific properties are modified. This enables:
 * 1. Simple mutable state updates (e.g., `state.isLoading = false`)
 * 2. Performance optimization by avoiding unnecessary renders when unused properties change
 *
 * @param initialValue The initial state object
 * @returns A proxy object that tracks property access and handles updates
 */
export const useProxyState = (initialValue) => {
    "use no memo";
    const [, render] = useReducer((a) => a + 1, 0);
    const rendered = useRef(false);
    const subbed = useRef(new Set());
    useEffect(() => {
        rendered.current = true;
        return () => {
            rendered.current = false;
        };
    }, []);
    // biome-ignore lint/correctness/useExhaustiveDependencies: We don't want to re-create the proxy on every render
    return useMemo(() => {
        let timeout = setTimeout(() => { }, 0);
        const toggleRender = () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => render(), 0);
        };
        return new Proxy(initialValue, {
            get(target, prop) {
                const key = prop;
                if (!rendered.current)
                    subbed.current.add(key);
                return target[key];
            },
            set(target, prop, value) {
                const key = prop;
                if (target[key] === value)
                    return true;
                target[key] = value;
                if (rendered.current && subbed.current.has(key))
                    toggleRender();
                return true;
            },
        });
    }, []);
};
//# sourceMappingURL=useProxyState.js.map